/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal, Form, Button, Alert, Spinner, Badge, Card,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import Error from '../../../Components/Error';
import { formatBookingDateOnly, formatBookingTimeOnly } from '../../../Utils/timezone';
import BookingService from '../../../Services/BookingService';
import OperatorService from '../../../Services/OperatorService';
import { assignableOperators } from '../../../Utils/operatorType';

const BOOKING_TYPE_LABELS = {
  transfer: { label: 'Transfer', color: 'primary', icon: 'fa-car' },
  experience: { label: 'Experience', color: 'info', icon: 'fa-map-marked-alt' },
};

function BatchOperatorInputModal({
  bookings: bookingsProp = [],
  show = false,
  handleClose,
  fetchOrders,
}) {
  // Ensure bookings is always an array - use useMemo to stabilize the reference
  const bookings = useMemo(
    () => (Array.isArray(bookingsProp) ? bookingsProp : []),
    [bookingsProp],
  );

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [operators, setOperators] = useState([]);
  const [decoratedOperators, setDecoratedOperators] = useState([]);
  const [error, setError] = useState('');

  // Per-booking assignments: { bookingId: { operator: [], price: '', currency: 'EUR' } }
  const [assignments, setAssignments] = useState({});

  // Track failed bookings: { bookingId: errorMessage }
  const [failedBookings, setFailedBookings] = useState({});

  const decorateOperatorOption = (option) => {
    if (!option) return null;
    return {
      id: option.Id,
      label: option.name,
    };
  };

  const fetchOperators = async () => {
    setLoading(true);
    try {
      const response = await OperatorService.fetchOperators();
      setOperators(response?.data.data || []);
    } catch (err) {
      setError('Failed to load operators');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) {
      fetchOperators();
      setError('');
      setFailedBookings({});
      // Initialize assignments with existing operator data
      const initialAssignments = {};
      bookings.forEach((booking) => {
        initialAssignments[booking._id] = {
          operator: booking.operator_id ? [{ id: booking.operator_id, label: booking.operator?.name || 'Unknown' }] : [],
          price: booking.operator_price || '',
          currency: booking.operator_currency || 'EUR',
        };
      });
      setAssignments(initialAssignments);
    }
  }, [show, bookings]);

  useEffect(() => {
    // ASSIGNMENT: every one of these typeaheads dispatches a booking to an
    // operator, so the own-fleet cost buckets are not offered. The label
    // lookup below still reads the UNFILTERED `operators`, so a booking
    // already carrying a bucket keeps showing its name.
    const operatorsTypeAhead = assignableOperators(operators).map(
      (op) => decorateOperatorOption(op),
    );
    setDecoratedOperators(operatorsTypeAhead);
  }, [operators]);

  // Update assignments with correct operator labels once operators are loaded
  // This is separate from the above effect to avoid unnecessary re-renders
  useEffect(() => {
    if (operators.length > 0 && bookings.length > 0) {
      setAssignments((prev) => {
        let hasChanges = false;
        const updated = { ...prev };
        bookings.forEach((booking) => {
          if (booking.operator_id) {
            const op = operators.find((o) => o.Id === booking.operator_id);
            if (op && (!updated[booking._id]?.operator?.length
                || updated[booking._id].operator[0].id !== op.Id)) {
              updated[booking._id] = {
                ...updated[booking._id],
                operator: [decorateOperatorOption(op)],
              };
              hasChanges = true;
            }
          }
        });
        // Only return new object if there were actual changes
        return hasChanges ? updated : prev;
      });
    }
  }, [operators, bookings]);

  const updateAssignment = (bookingId, field, value) => {
    setAssignments((prev) => ({
      ...prev,
      [bookingId]: {
        ...prev[bookingId],
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    // Validate all bookings have an operator assigned
    const missingOperators = bookings.filter(
      (b) => !assignments[b._id]?.operator?.length,
    );
    if (missingOperators.length > 0) {
      setError(`Please assign an operator to all bookings (${missingOperators.length} missing)`);
      return;
    }

    setSubmitting(true);
    setFailedBookings({});
    try {
      // Build assignments array with each booking's specific operator details
      const assignmentsArray = bookings.map((booking) => {
        const assignment = assignments[booking._id];
        return {
          booking_id: booking._id,
          operator_id: assignment.operator[0].id,
          operator_price: assignment.price ? Number(assignment.price) : undefined,
          operator_currency: assignment.currency || undefined,
        };
      });

      // Single API call with all assignments
      const result = await BookingService.bulkAssignOperator(assignmentsArray);

      // Check for partial failures
      if (result?.errors?.length > 0) {
        const failed = {};
        result.errors.forEach((err) => {
          failed[err.bookingId] = err.error;
        });
        setFailedBookings(failed);
        setError(`${result.updatedCount} succeeded, ${result.errors.length} failed. See highlighted rows below.`);
        fetchOrders();
      } else {
        // All succeeded
        fetchOrders();
        handleClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign operators');
    } finally {
      setSubmitting(false);
    }
  };

  // Apply same operator to all bookings
  const applyToAll = (bookingId) => {
    const sourceAssignment = assignments[bookingId];
    if (!sourceAssignment?.operator?.length) return;

    setAssignments((prev) => {
      const updated = { ...prev };
      bookings.forEach((booking) => {
        updated[booking._id] = { ...sourceAssignment };
      });
      return updated;
    });
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          Batch Assign Operators
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <i className="fa fa-info-circle me-2" />
          Assign operators to
          {' '}
          <Badge bg="primary">{bookings.length}</Badge>
          {' '}
          booking(s).
        </Alert>

        <Error error={error} setError={setError} />

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-2">Loading operators...</p>
          </div>
        ) : (
          <div>
            {bookings.map((booking) => (
              <Card
                key={booking._id}
                className={`mb-2 ${failedBookings[booking._id] ? 'border-danger' : ''}`}
              >
                <Card.Body className="py-2 px-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">{booking.full_name || 'Unknown'}</span>
                        {(() => {
                          const typeInfo = BOOKING_TYPE_LABELS[booking.booking_type]
                            || BOOKING_TYPE_LABELS.transfer;
                          return (
                            <Badge bg={typeInfo.color} style={{ fontSize: '0.7em' }}>
                              <i className={`fa ${typeInfo.icon}`} />
                            </Badge>
                          );
                        })()}
                      </div>
                      <small className="text-muted">
                        {booking.date_datetime
                          ? `${formatBookingDateOnly(booking, 'date_datetime')} ${formatBookingTimeOnly(booking, 'date_datetime')}`
                          : '-'}
                      </small>
                    </div>
                    {assignments[booking._id]?.operator?.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        title="Apply to all"
                        onClick={() => applyToAll(booking._id)}
                      >
                        <i className="fa fa-copy" />
                      </Button>
                    )}
                  </div>
                  {failedBookings[booking._id] && (
                    <div className="text-danger small mb-2">
                      <i className="fa fa-exclamation-circle me-1" />
                      {failedBookings[booking._id]}
                    </div>
                  )}
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Operator</Form.Label>
                    <Typeahead
                      size="sm"
                      placeholder="Select operator"
                      id={`operator_${booking._id}`}
                      onChange={(selected) => updateAssignment(booking._id, 'operator', selected)}
                      options={decoratedOperators}
                      selected={assignments[booking._id]?.operator || []}
                      positionFixed
                    />
                  </Form.Group>
                  <div className="row g-2">
                    <div className="col-6">
                      <Form.Group>
                        <Form.Label className="small mb-1">Price</Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          placeholder="Price"
                          value={assignments[booking._id]?.price || ''}
                          onChange={(e) => updateAssignment(booking._id, 'price', e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className="col-6">
                      <Form.Group>
                        <Form.Label className="small mb-1">Currency</Form.Label>
                        <Form.Select
                          size="sm"
                          value={assignments[booking._id]?.currency || 'EUR'}
                          onChange={(e) => updateAssignment(booking._id, 'currency', e.target.value)}
                        >
                          <option value="EUR">EUR</option>
                          <option value="HUF">HUF</option>
                        </Form.Select>
                      </Form.Group>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={submitting || loading}>
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" />
              Assigning...
            </>
          ) : (
            <>
              <i className="fa fa-building me-2" />
              Assign Operators
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default BatchOperatorInputModal;
